// src/pages/lead-automation/LeadAutomationPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Upload,
  UploadCloud,
  History,
  Loader,
  AlertCircle,
  RotateCcw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  getLeadForms,
  createLeadForm,
  updateLeadForm,
  deleteLeadForm,
  triggerUpload,
  resetCursor,
  getGoogleConnectionStatus,
  getGoogleAuthUrl,
  disconnectGoogle,
} from '../../services/api/leadAutomation';
import GoogleConnectBanner from './components/GoogleConnectBanner';
import LeadFormModal from './components/LeadFormModal';
import styles from './LeadAutomationPage.module.css';

const LeadAutomationPage = () => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const la = (key) => t(`leadAutomation.${key}`);

  const canManage = hasPermission('manage_lead_forms');

  // Google connection
  const [googleStatus, setGoogleStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  // Lead forms
  const [leadForms, setLeadForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Modal
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit'
  const [editingForm, setEditingForm] = useState(null);

  // Upload state per form
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadResult, setUploadResult] = useState({}); // { [formId]: { success, ... } }

  // Upload-all state
  const [uploadingAll, setUploadingAll] = useState(false);

  // Reset cursor state
  const [resetingId, setResetingId] = useState(null);

  // Expanded error details per form
  const [expandedErrorFormId, setExpandedErrorFormId] = useState(null);

  // ── Load data ────────────────────────────────────────────────────────────────

  const loadGoogleStatus = useCallback(async () => {
    try {
      const data = await getGoogleConnectionStatus();
      setGoogleStatus(data);
    } catch {
      setGoogleStatus({ connected: false });
    }
  }, []);

  const loadLeadForms = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await getLeadForms();
      setLeadForms(data.leadForms || data || []);
    } catch (err) {
      setLoadError(err?.response?.data?.message || la('failedToLoadLeadForms'));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadGoogleStatus();
    loadLeadForms();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle OAuth callback redirect (Google redirects to frontend with ?google=connected)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      success(la('googleConnectedSuccess'));
      loadGoogleStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('error')) {
      showError(decodeURIComponent(params.get('error')) || la('failedToConnect'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Google OAuth ─────────────────────────────────────────────────────────────

  const handleConnectGoogle = async () => {
    setConnectLoading(true);
    try {
      const data = await getGoogleAuthUrl();
      window.location.href = data.url;
    } catch {
      showError(la('failedToConnect'));
      setConnectLoading(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    const confirmed = await confirm(
      la('disconnectConfirmTitle'),
      la('disconnectConfirmDescription')
    );
    if (!confirmed) return;
    setDisconnectLoading(true);
    try {
      await disconnectGoogle();
      success(la('googleDisconnectedSuccess'));
      loadGoogleStatus();
    } catch {
      showError(la('failedToDisconnect'));
    } finally {
      setDisconnectLoading(false);
    }
  };

  // ── CRUD ─────────────────────────────────────────────────────────────────────

  const handleCreateSubmit = async (payload) => {
    try {
      await createLeadForm(payload);
      success(la('leadFormCreated'));
      setModalMode(null);
      loadLeadForms();
    } catch (err) {
      showError(err?.response?.data?.message || la('failedToCreate'));
      throw err;
    }
  };

  const handleEditSubmit = async (payload) => {
    try {
      await updateLeadForm(editingForm.id, payload);
      success(la('leadFormUpdated'));
      setModalMode(null);
      setEditingForm(null);
      loadLeadForms();
    } catch (err) {
      showError(err?.response?.data?.message || la('failedToUpdate'));
      throw err;
    }
  };

  const handleDelete = async (form) => {
    const confirmed = await confirm(la('confirmDelete'), la('confirmDeleteDescription'));
    if (!confirmed) return;
    try {
      await deleteLeadForm(form.id);
      success(la('leadFormDeleted'));
      loadLeadForms();
    } catch (err) {
      showError(err?.response?.data?.message || la('failedToDelete'));
    }
  };

  // ── Upload ────────────────────────────────────────────────────────────────────

  const handleUpload = async (form) => {
    if (uploadingId) return;
    setUploadingId(form.id);
    setUploadResult((prev) => ({ ...prev, [form.id]: null }));
    try {
      const data = await triggerUpload(form.id);
      setUploadResult((prev) => ({ ...prev, [form.id]: data }));
      loadLeadForms(); // refresh lastRowIndex
    } catch (err) {
      const msg = err?.response?.data?.message || la('failedToUpload');
      showError(msg);
    } finally {
      setUploadingId(null);
    }
  };

  const handleUploadAll = async () => {
    if (uploadingId || uploadingAll) return;
    const activeForms = leadForms.filter((form) => form.status === 'active');
    if (activeForms.length === 0) return;
    if (!googleStatus?.connected) {
      showError(la('googleNotConnectedError'));
      return;
    }

    setUploadingAll(true);
    for (const form of activeForms) {
      setUploadingId(form.id);
      setUploadResult((prev) => ({ ...prev, [form.id]: null }));
      try {
        const data = await triggerUpload(form.id);
        setUploadResult((prev) => ({ ...prev, [form.id]: data }));
      } catch (err) {
        const msg = err?.response?.data?.message || la('failedToUpload');
        showError(`${form.name}: ${msg}`);
      }
    }
    setUploadingId(null);
    setUploadingAll(false);
    loadLeadForms(); // refresh lastRowIndex for all forms
    success(la('uploadAllComplete'));
  };

  // ── Reset cursor ──────────────────────────────────────────────────────────────

  const handleResetCursor = async (form) => {
    const confirmed = await confirm(la('resetCursorConfirm'));
    if (!confirmed) return;
    setResetingId(form.id);
    try {
      await resetCursor(form.id);
      success(la('cursorResetSuccess'));
      loadLeadForms();
    } catch (err) {
      showError(err?.response?.data?.message || la('failedToResetCursor'));
    } finally {
      setResetingId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const renderUploadResult = (formId) => {
    const result = uploadResult[formId];
    if (!result) return null;

    const session = result.session || result;
    const errorDetails = result.errorDetails || [];
    const isSuccess = session.successCount > 0 || session.skippedCount > 0 || session.duplicateCount > 0;
    const isExpanded = expandedErrorFormId === formId;

    return (
      <div className={styles.uploadResultWrap}>
        <div className={`${styles.uploadResult} ${isSuccess ? styles.uploadResultSuccess : styles.uploadResultWarning}`}>
          {isSuccess ? (
            <CheckCircle size={13} />
          ) : (
            <AlertTriangle size={13} />
          )}
          <span>
            {la('uploadResult.created')}: <strong>{session.successCount}</strong>
            {' · '}
            {la('uploadResult.skipped')}: <strong>{session.skippedCount}</strong>
            {' · '}
            {la('uploadResult.duplicate')}: <strong>{session.duplicateCount}</strong>
            {' · '}
            {la('uploadResult.error')}: <strong>{session.errorCount}</strong>
          </span>
          {session.errorCount > 0 && errorDetails.length > 0 && (
            <button
              className={styles.errorToggleBtn}
              onClick={() => setExpandedErrorFormId(isExpanded ? null : formId)}
            >
              {isExpanded ? la('hideErrors') : la('showErrors')}
              <ChevronDown size={12} className={isExpanded ? styles.chevronUp : ''} />
            </button>
          )}
        </div>
        {isExpanded && errorDetails.length > 0 && (
          <ul className={styles.errorDetailList}>
            {errorDetails.map((d, i) => (
              <li key={i} className={styles.errorDetailItem}>
                <span className={styles.errorRowLabel}>{la('rowLabel')} {d.rowNumber}:</span>
                {' '}{Array.isArray(d.errors) ? d.errors.join('; ') : d.errors}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const activeFormCount = leadForms.filter((form) => form.status === 'active').length;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{la('manageLeadForms')}</h1>
          <p className={styles.pageDesc}>{la('pageDescription')}</p>
        </div>
        <div className={styles.headerActions}>
          {hasPermission('create_ecom_order') && activeFormCount > 0 && (
            <button
              className={styles.uploadAllBtn}
              onClick={handleUploadAll}
              disabled={uploadingAll || !!uploadingId || !googleStatus?.connected}
              title={!googleStatus?.connected ? la('googleNotConnectedError') : undefined}
            >
              {uploadingAll ? (
                <><Loader size={16} className={styles.spin} /> {la('uploadingAll')}</>
              ) : (
                <><UploadCloud size={16} /> {la('uploadAllActive')}</>
              )}
            </button>
          )}
          {canManage && (
            <button className={styles.createBtn} onClick={() => setModalMode('create')}>
              <Plus size={16} />
              {la('createLeadForm')}
            </button>
          )}
        </div>
      </div>

      <GoogleConnectBanner
        status={googleStatus}
        onConnect={handleConnectGoogle}
        onDisconnect={handleDisconnectGoogle}
        connectLoading={connectLoading}
        disconnectLoading={disconnectLoading}
      />

      {loading && (
        <div className={styles.loadingState}>
          <Loader size={20} className={styles.spin} />
        </div>
      )}

      {!loading && loadError && (
        <div className={styles.errorState}>
          <AlertCircle size={16} />
          {loadError}
        </div>
      )}

      {!loading && !loadError && leadForms.length === 0 && (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>{la('noLeadFormsYet')}</p>
          <p className={styles.emptyDesc}>{la('noLeadFormsYetDescription')}</p>
          {canManage && (
            <button className={styles.createBtn} onClick={() => setModalMode('create')}>
              <Plus size={16} />
              {la('createFirstLeadForm')}
            </button>
          )}
        </div>
      )}

      {!loading && !loadError && leadForms.length > 0 && (
        <div className={styles.formList}>
          {leadForms.map((form) => (
            <div key={form.id} className={`${styles.card} ${form.status === 'paused' ? styles.cardPaused : ''}`}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitleRow}>
                  <h3 className={styles.cardTitle}>{form.name}</h3>
                  <span className={`${styles.statusBadge} ${form.status === 'active' ? styles.statusActive : styles.statusPaused}`}>
                    {form.status === 'active' ? la('statusActive') : la('statusPaused')}
                  </span>
                </div>
                {canManage && (
                  <div className={styles.cardActions}>
                    <button
                      className={styles.iconBtn}
                      title={la('editLeadForm')}
                      onClick={() => { setEditingForm(form); setModalMode('edit'); }}
                    >
                      <Edit size={15} />
                    </button>
                    <button
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      title={la('deleteLeadForm')}
                      onClick={() => handleDelete(form)}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.cardMeta}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{la('sheetTab')}</span>
                  <span className={styles.metaValue}>{form.sheetTabName}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{la('defaultShop')}</span>
                  <span className={styles.metaValue}>{form.shop?.name || '—'}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{la('defaultProduct')}</span>
                  <span className={styles.metaValue}>{form.defaultProduct?.name || '—'}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{la('duplicateCheckDays')}</span>
                  <span className={styles.metaValue}>{form.duplicateCheckDays} {la('days')}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{la('lastRow')}</span>
                  <span className={styles.metaValue}>{form.lastRowIndex}</span>
                </div>
              </div>

              {renderUploadResult(form.id)}

              <div className={styles.cardFooter}>
                <div className={styles.footerActions}>
                  {hasPermission('create_ecom_order') && (
                    <button
                      className={styles.uploadBtn}
                      onClick={() => handleUpload(form)}
                      disabled={!!uploadingId || uploadingAll || form.status === 'paused' || !googleStatus?.connected}
                      title={!googleStatus?.connected ? la('googleNotConnectedError') : undefined}
                    >
                      {uploadingId === form.id ? (
                        <><Loader size={14} className={styles.spin} /> {la('uploading')}</>
                      ) : (
                        <><Upload size={14} /> {la('uploadLeads')}</>
                      )}
                    </button>
                  )}

                  <button
                    className={styles.secondaryBtn}
                    onClick={() => navigate(`/lead-automation/${form.id}/sessions`)}
                  >
                    <History size={14} />
                    {la('viewSessions')}
                  </button>

                  {canManage && (
                    <button
                      className={styles.secondaryBtn}
                      onClick={() => handleResetCursor(form)}
                      disabled={resetingId === form.id}
                      title={la('resetCursorConfirm')}
                    >
                      {resetingId === form.id ? (
                        <Loader size={14} className={styles.spin} />
                      ) : (
                        <RotateCcw size={14} />
                      )}
                      {la('resetCursor')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {modalMode === 'create' && (
        <LeadFormModal
          mode="create"
          leadForm={null}
          onClose={() => setModalMode(null)}
          onSuccess={handleCreateSubmit}
        />
      )}

      {/* Edit Modal */}
      {modalMode === 'edit' && editingForm && (
        <LeadFormModal
          mode="edit"
          leadForm={editingForm}
          onClose={() => { setModalMode(null); setEditingForm(null); }}
          onSuccess={handleEditSubmit}
        />
      )}
    </div>
  );
};

export default LeadAutomationPage;
